const INFIX_OPS = '+-×÷*/^';

function isModAt(s: string, i: number): boolean {
  if (i + 2 >= s.length) return false;
  if (s.slice(i, i + 3).toLowerCase() !== 'mod') return false;
  const before = i > 0 ? s[i - 1]! : '';
  const after = i + 3 < s.length ? s[i + 3]! : '';
  if (before && /[a-zA-Z]/.test(before)) return false;
  if (after && /[a-zA-Z]/.test(after)) return false;
  return true;
}

function findLastSplitIndex(s: string): number {
  let depth = 0;
  for (let i = s.length - 1; i >= 1; i--) {
    const ch = s[i]!;
    if (ch === ')') depth++;
    else if (ch === '(') depth--;
    else if (depth === 0) {
      if (INFIX_OPS.includes(ch)) return i;
      if (i >= 2 && isModAt(s, i - 2)) return i - 2;
    }
  }
  return -1;
}

/** Split base vs exponent at the last `^` (handles `2^22`, `10^22`, `2+10^3`). */
function splitAtPower(s: string): { prefix: string; entry: string } | null {
  const idx = s.lastIndexOf('^');
  if (idx < 0) return null;
  return { prefix: s.slice(0, idx + 1), entry: s.slice(idx + 1) };
}

/** Operand inside open `log10(` / `ln(` (e.g. `2-log10(3` → entry `3`). */
function splitLogLnOpen(s: string): { prefix: string; entry: string } | null {
  const m = s.match(/^(.*?)(log10|ln)\(([^)]*)$/i);
  if (!m) return null;
  return { prefix: m[1] + m[2] + '(', entry: m[3] ?? '' };
}

function splitForEntry(s: string): { prefix: string; entry: string } {
  const logLn = splitLogLnOpen(s);
  if (logLn) return logLn;

  const splitAt = findLastSplitIndex(s);
  if (splitAt >= 0) {
    const opLen = isModAt(s, splitAt) ? 3 : 1;
    const prefix = s.slice(0, splitAt + opLen);
    const entry = s.slice(splitAt + opLen);
    const op = s[splitAt]!;
    // e.g. 2^22- : split at trailing −, not inside exponent → entry is empty
    if (!entry && op !== '^') {
      return { prefix: s, entry: '' };
    }
    if (entry === 'log10' || entry === 'ln') {
      return { prefix: s, entry: '' };
    }
    return { prefix, entry };
  }
  const pow = splitAtPower(s);
  if (pow) return pow;
  return { prefix: '', entry: s };
}

/** Backspace one step; remove broken `log1` tails from partial `log10(`. */
export function backspaceExpression(expr: string): string {
  if (!expr) return '';
  let s = expr.slice(0, -1);

  const partialLog = s.match(/(.*)(log\d*)$/i);
  if (partialLog) {
    const tail = partialLog[2]!;
    if (tail.startsWith('log') && tail !== 'log10') {
      return partialLog[1]!;
    }
  }

  if (/\bln$/i.test(s) && !/\bln\(/i.test(s)) {
    const m = s.match(/(.*)ln$/i);
    if (m) return m[1]!;
  }

  return s;
}

/** Format raw expression for the formula line (Win11 style with spaced operators) */
export function formatFormulaDisplay(raw: string): string {
  if (!raw) return '';
  const s = raw.replace(/\s+/g, '');
  const parts: string[] = [];
  let i = 0;
  while (i < s.length) {
    const ch = s[i]!;
    if (INFIX_OPS.includes(ch)) {
      const op =
        ch === '*'
          ? '×'
          : ch === '/'
            ? '÷'
            : ch === '-'
              ? '−'
              : ch === '+'
                ? '+'
                : ch === '^'
                  ? '^'
                  : ch;
      parts.push(op);
      i++;
      continue;
    }
    if (isModAt(s, i)) {
      parts.push('mod');
      i += 3;
      continue;
    }
    let j = i;
    while (j < s.length && !INFIX_OPS.includes(s[j]!) && !isModAt(s, j)) j++;
    parts.push(s.slice(i, j));
    i = j;
  }
  return parts.join(' ').replace(/^\− /, '−');
}

/** Append digit/`.` to expression with calculator-style leading-zero rules */
export function appendToExpression(expr: string, token: string): string {
  const s = expr.replace(/\s+/g, '');

  if (/^[0-9]$/.test(token)) {
    if (!s) return token === '0' ? '0' : token;

    const { prefix, entry } = splitForEntry(s);

    if (token === '0') {
      if (entry === '' || entry === '0' || entry === '-0') return prefix + (entry || '0');
      return s + token;
    }

    // 1–9: replace lone 0 (not 0.xxx)
    if (entry === '' || entry === '0') return prefix + token;
    if (entry === '-0' || entry === '-') return prefix + '-' + token;
    return s + token;
  }

  if (token === '.') {
    if (!s) return '0.';
    const { prefix, entry } = splitForEntry(s);
    if (entry.includes('.')) return s;
    if (entry === '' || entry === '-') return prefix + entry + '0.';
    return prefix + entry + '.';
  }

  return s + token;
}

export function getCurrentEntry(expr: string): string {
  const s = expr.replace(/\s+/g, '');
  if (!s) return '0';

  const { entry } = splitForEntry(s);
  if (entry !== s) return entry || '0';
  return s;
}

/** Replace only the trailing operand (e.g. `20-3` + factorial → `20-6`) */
export function replaceCurrentOperand(expr: string, newOperand: string): string {
  const s = expr.replace(/\s+/g, '');
  if (!s) return newOperand;
  const splitAt = findLastSplitIndex(s);
  if (splitAt >= 0) {
    const opLen = isModAt(s, splitAt) ? 3 : 1;
    return s.slice(0, splitAt + opLen) + newOperand;
  }
  return newOperand;
}

export function hasBinaryOperator(expr: string): boolean {
  const s = expr.replace(/\s+/g, '');
  if (!s) return false;
  if (/[×÷+*/^()]/.test(s)) return true;
  if (/mod/i.test(s)) return true;
  const dash = s.indexOf('-');
  return dash > 0;
}